import { defineStore } from 'pinia'
import { ref } from 'vue'

import { ToastMessageOptions } from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

import type { ToastEvent } from './models/toastEvent'
import { EMAIL_VERIFICATION_TOAST_GROUP } from '../shared/constants/toast'

interface ToastOptions {
  sticky?: boolean
}

interface ToastType {
  severity?:
    | 'success'
    | 'info'
    | 'warn'
    | 'error'
    | 'secondary'
    | 'contrast'
    | undefined
  summary?: string
  detail?: string | undefined
}

export const useEventStore = defineStore('EventStore', () => {
  const toast = useToast()

  const addToast = (
    message: ToastMessageOptions,
    options: ToastOptions = {},
  ): void => {
    toast.add({
      ...message,
      life: options.sticky ? undefined : (message.life ?? 3500),
    })
  }

  const addCreateToast = (
    data: ToastType = {
      summary: 'Created',
      detail: undefined,
      severity: 'success',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addUpdateToast = (
    data: ToastType = {
      summary: 'Updated',
      detail: undefined,
      severity: 'success',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addDeleteToast = (
    data: ToastType = {
      summary: 'Deleted',
      detail: undefined,
      severity: 'info',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addArchiveToast = (
    data: ToastType = {
      summary: 'Archived',
      detail: undefined,
      severity: 'info',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addRestoreToast = (
    data: ToastType = {
      summary: 'Restored',
      detail: undefined,
      severity: 'success',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addSyncToast = (
    data: ToastType = {
      summary: 'Synced',
      detail: undefined,
      severity: 'success',
    },
  ): void => {
    addToast({
      severity: data.severity,
      summary: data.summary,
      detail: data.detail,
    })
  }

  const addErrorToast = (data: ToastType): void => {
    addToast({
      severity: data.severity ?? 'error',
      summary: data.summary ?? 'error',
      detail: data.detail,
    })
  }

  const removeToast = (message: ToastMessageOptions): void => {
    toast.remove(message)
  }

  const removeGroup = (group: string): void => {
    toast.removeGroup(group)
  }

  const removeAllGroups = (): void => {
    toast.removeAllGroups()
  }

  const addHintToast = (data: ToastType): void => {
    removeHintToast()

    toast.add({
      severity: data.severity ?? 'secondary',
      detail: data.detail,
      group: 'hint',
    })
  }

  const removeHintToast = (): void => {
    toast.removeGroup('hint')
  }

  const addSessionExpiredToast = (): void => {
    addToast({
      severity: 'error',
      summary: 'Session Expired',
      detail: 'Your session has expired. Please log in again.',
    })
  }

  const addPasswordExpiredToast = (): void => {
    addToast({
      severity: 'error',
      summary: 'Password Expired',
      detail: 'Your password has expired. Please reset your password.',
    })
  }

  const events = ref<ToastEvent[]>([])

  function addEvent(event: ToastEvent, duration: number = 3500): void {
    toast.add({
      severity: event.severity,
      summary: event.subject,
      detail: event.message,
      life: duration,
    })

    events.value.push(event)

    if (duration !== null) {
      setTimeout(() => {
        removeEvent(event)
      }, duration)
    }
  }

  function removeEvent(event: ToastEvent): void {
    const index = events.value.indexOf(event)

    if (index !== -1) {
      events.value.splice(index, 1)
    }
  }

  function showEmailVerificationToast(): void {
    addToast({
      severity: 'secondary',
      summary: 'Email Not Verified',
      detail: 'Please verify your email address to access all features.',
      group: EMAIL_VERIFICATION_TOAST_GROUP,
    }, { sticky: true })
  }

  return {
    events,
    addEvent,
    removeEvent,
    addToast,
    addCreateToast,
    addUpdateToast,
    addDeleteToast,
    addArchiveToast,
    addRestoreToast,
    addSyncToast,
    addErrorToast,
    removeToast,
    removeGroup,
    removeAllGroups,
    addHintToast,
    removeHintToast,
    addSessionExpiredToast,
    addPasswordExpiredToast,
    showEmailVerificationToast,
  }
})
